// File: frontend/src/api/players.ts
// REVISED: Updated to handle server-side pagination, sorting, and filtering parameters.

import { API_BASE_URL } from '@/config/apiConfig';
import { ApiError } from './auth';
import { createApiClient, isAxiosError } from './client';
import { PaginatedLeaguePlayersResponse } from './schemas';

const logger = console; // Use the browser's console for logging

const playersApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Interface for the parameters object, now including search and position
export interface FetchAvailableLeaguePlayersParams {
    leagueId: number;
    token: string;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    position?: string;
    search?: string;
    // --- NEW ---
    availability?: 'available' | 'rostered' | 'all';
}

export const fetchAvailablePlayersForLeague = async ({
    leagueId,
    token,
    skip = 0,
    limit = 50,
    sort_by = "fantasy_points", // Default sort to new field
    sort_order = "desc",
    position,
    search,
    availability = "available", // Default to available
}: FetchAvailableLeaguePlayersParams): Promise<PaginatedLeaguePlayersResponse> => {
  if (!token) throw new ApiError("Authentication required to fetch available players.", 401);

  const queryParams = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      sort_by: sort_by,
      sort_order: sort_order,
      availability: availability,
  });

  if (position && position !== 'ALL') {
    queryParams.append('position', position);
  }
  if (search) {
    queryParams.append('search', search);
  }

  const url = `${API_BASE_URL}/leagues/${leagueId}/available-players?${queryParams.toString()}`;
  logger.info(`[API] Fetching paginated available league players. URL: ${url}`);

  try {
    const response = await playersApiClient.get<PaginatedLeaguePlayersResponse>(url, {
        headers: { Authorization: `Bearer ${token}` }
    });
    logger.info(`[API] Received paginated players for L:${leagueId}. Players: ${response.data.players.length}, Total: ${response.data.total_available}, HasMore: ${response.data.has_more}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch available players for league ${leagueId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 404) {
        logger.warn(`[API] League ${leagueId} or available players not found (404 response). Returning empty paginated structure.`);
        return { players: [], total_available: 0, limit: limit, skip: skip, has_more: false };
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    logger.error(`[API] Error fetching available players for league ${leagueId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};
